import { useRef, useState } from 'react';
import classnames from 'classnames';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import DeviceManager from '../../../util/deviceManager';
import { getResizeOptions } from './ResizeMinimap';
import { ResizableStyled } from './styles';

function ControlButton({ className, iconClass, onClick }: any) {
  return (
    <button className={classnames('control-btn', className)} onClick={onClick}>
      <i className={iconClass} />
    </button>
  );
}

function HotspotButton({ hotspot, markerClick, hideMinimapHotspots, custom }: any) {
  const hotspotNotActive = hideMinimapHotspots ? 'marker hide' : 'marker';
  return (
    !custom && (
      <span
        key={hotspot.id}
        onClick={() => markerClick(hotspot)}
        role="button"
        tabIndex={0}
        onKeyPress={() => null}
        className={hotspot.active ? 'marker active' : hotspotNotActive}
        style={{
          top: `${hotspot.y}%`,
          left: `${hotspot.x}%`,
        }}
      >
        <i className="fas fa-bullseye" />
      </span>
    )
  );
}

export default function Minimap(props: any) {
  const [degrees, setDegrees] = useState(0);
  const [rotationDirection, setRotationDirection] = useState('zero');
  const [isNarrow, setIsNarrow] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const minimapRef = useRef(null);
  let zoomInHandler;
  let zoomOutHandler;

  const { tour, minimapVisible, onClick, minimapOn, youtubeOn, markerClick, minimaps, selectedMinimap, mapWalkerX, mapWalkerY, mapWalkerRotation } = props;

  if (!tour.minimap || !minimapVisible) return null;

  const isReelOpen =
    document.getElementById('bottom-bar')?.classList.contains('open') ||
    document.getElementById('ecommerce-bottom-bar')?.classList.contains('open') ||
    document.getElementById('game-bottom-bar')?.classList.contains('open');
  const isActive = minimapOn && !youtubeOn;
  const minimap = minimaps[selectedMinimap];
  const baseAWSURL = `${process.env.REACT_APP_AWS}/tours/${minimap && minimap.tourId}/minimaps`;

  const handleMinimapImageLoad = () => {
    setIsNarrow(imgRef.current?.height > imgRef.current?.width);
  };

  const toggleActive = () => {
    if (minimapRef?.current) {
      const { style } = minimapRef.current.resizable;
      if (!style) return;

      minimapRef.current.resizable.style.width = '';
      if (isActive) {
        minimapRef.current.resizable.style.height = minimapRef.current.resizable.style.minHeight;
        onClick();
      } else {
        onClick();
        minimapRef.current.resizable.style.height = minimapRef.current.resizable.style.maxHeight;
      }
    }
  };

  const rotate = (direction: string) => {
    let degreesNew = degrees;
    degreesNew += direction === 'right' ? 90 : -90;
    setDegrees(degreesNew);
    let check = degreesNew % 360;
    check /= 90;
    // eslint-disable-next-line no-nested-ternary
    let newRotationDirection = check === 0 ? 'zero' : check === 1 || check === -3 ? 'ninety' : check === 2 || check === -2 ? 'flip' : 'minusNinety';
    if (newRotationDirection === 'minusNinety' && rotationDirection === 'flip') {
      newRotationDirection = 'twoSeventy';
    }
    setRotationDirection(newRotationDirection);
  };

  const zoom = (direction: string) => {
    // zoomInHandler, zoomOutHandler callback handlers take not null
    const event = {};
    if (direction === 'in') {
      zoomInHandler(event);
    } else {
      zoomOutHandler(event);
    }
  };

  const buttons = [
    {
      className: 'fullscreen',
      iconClass: isActive ? 'fas fa-compress' : 'fas fa-expand',
      function: toggleActive,
    },
    {
      className: 'zoom-in',
      iconClass: 'fas fa-search-plus',
      function: () => zoom('in'),
    },
    {
      className: 'zoom-out',
      iconClass: 'fas fa-search-minus',
      function: () => zoom('out'),
    },
    {
      className: 'rotate-right',
      iconClass: 'fas fa-undo',
      function: () => rotate('right'),
    },
    {
      className: 'rotate-left',
      iconClass: 'fas fa-undo',
      function: () => rotate('left'),
    },
  ];

  const zoomOptions = {
    minScale: 1,
    maxScale: 4,
  };

  return (
    <>
      <ResizableStyled
        role="button"
        tabIndex={0}
        onKeyPress={() => null}
        className={classnames('minimap', {
          active: isActive,
          narrow: isNarrow,
          mobile: DeviceManager.isMobile(),
          'reel-open': isReelOpen,
        })}
        ref={minimapRef}
        {...getResizeOptions(isNarrow)}
      >
        {minimaps.length > 0 && minimap && (
          <div className="minimapC">
            <TransformWrapper defaultScale={1} options={zoomOptions}>
              {({ zoomIn, zoomOut }, index) => {
                zoomInHandler = zoomIn;
                zoomOutHandler = zoomOut;
                return (
                  <TransformComponent key={index}>
                    <div className={classnames('image-container', rotationDirection)}>
                      <img onLoad={handleMinimapImageLoad} ref={imgRef} src={`${baseAWSURL}/${minimap.customUrl ? minimap.customUrl : minimap.url}`} alt="" />
                      {!tour.customMinimap && !minimap.custom && mapWalkerX !== null && mapWalkerY !== null && (
                        <span
                          id="mapWalker"
                          style={{
                            transform: `rotate(${mapWalkerRotation}deg)`,
                            left: `${mapWalkerX}%`,
                            top: `${mapWalkerY}%`,
                          }}
                        >
                          <i className="fas fa-arrow-alt-circle-up" />
                        </span>
                      )}
                      {minimap.hotspots.map((hotspot: any) => (
                        <HotspotButton key={hotspot.id} hideMinimapHotspots={tour.hideMinimapHotspots} hotspot={hotspot} markerClick={markerClick} />
                      ))}
                    </div>
                  </TransformComponent>
                );
              }}
            </TransformWrapper>
          </div>
        )}
        <div className="control-buttons">
          {buttons.map((button: any) => (
            <ControlButton key={button.className} className={button.className} iconClass={button.iconClass} onClick={button.function} />
          ))}
        </div>
      </ResizableStyled>
    </>
  );
}
